import { useMemo, useState, useCallback } from 'react';
import useAuth from "../../hooks/useAuth";
import SummaryCriteriaAnalyticsTable from './SummaryCriteriaAnalyticsTable';
import SendReportModal from './SendReportModal';

const SummaryAnalyticsContent = ({ data, dateFrom, dateTo }) => {
  const { projects: allProjects } = useAuth();
  const [selectedProjects, setSelectedProjects] = useState([]); // Выбранные проекты
  const [showSendReportModal, setShowSendReportModal] = useState(false); // Модальное окно отправки отчета
  const [criteriaData, setCriteriaData] = useState([]); // Данные по критериям из SummaryCriteriaAnalyticsTable
  const [criteriaColumns, setCriteriaColumns] = useState([]); // Колонки для таблицы критериев
  // Извлечение уникальных project_id из данных
  const projectIds = useMemo(() => {
    if (!data.length) return [];
    const idSet = new Set();
    data.forEach(item => {
      const projectId = item.assessment.project_id;
      if (projectId) {
        idSet.add(projectId);
      }
    });
    return Array.from(idSet);
  }, [data]);

  // Формирование списка проектов из allProjects по projectIds
  const projects = useMemo(() => {
    if (!allProjects || !projectIds.length) return [];
    return allProjects.filter(project => projectIds.includes(project.id));
  }, [allProjects, projectIds]);

  // Фильтрация данных по выбранным проектам
  const filteredData = useMemo(() => {
    if (selectedProjects.length === 0) return data;
    return data.filter(item => 
      selectedProjects.includes(item.assessment.project_id)
    );
  }, [data, selectedProjects]);

  // Статистика по Hard и Soft skill
  const stats = useMemo(() => {
    let hardCount = 0;
    let softCount = 0;
    let hardScoreSum = 0;
    let softScoreSum = 0;

    filteredData.forEach(item => {
      const assessment = item.assessment;
      if (assessment.total_count_hard > 0) {
        hardCount += 1;
        hardScoreSum += assessment.total_score_hard || 0;
      }
      if (assessment.total_count_soft > 0) {
        softCount += 1;
        softScoreSum += assessment.total_score_soft || 0;
      }
    });

    const hardAvg = hardCount > 0 ? (hardScoreSum / hardCount * 100).toFixed(2) : '0.00';
    const softAvg = softCount > 0 ? (softScoreSum / softCount * 100).toFixed(2) : '0.00';

    return { hardCount, softCount, hardAvg, softAvg };
  }, [filteredData]);

  // Расчет агрегированных значений для отфильтрованных данных
  const aggregatedData = useMemo(() => {
    if (!filteredData.length) {
      return { totalAssessments: 0, avgScore: 0 };
    }
    
    const totalScore = filteredData.reduce((sum, item) => sum + item.assessment.total_score, 0);
    const avgScore = filteredData.length > 0 ? (totalScore / filteredData.length) * 100 : 0;
    
    return {
      totalAssessments: filteredData.length,
      avgScore: avgScore.toFixed(2)
    };
  }, [filteredData]);

  // Расчет среднего балла для конкретного проекта
  const getProjectAvgScore = (projectId) => {
    const projectData = data.filter(item => 
      item.assessment.project_id === projectId
    );
    
    if (!projectData.length) return 0;
    
    const totalScore = projectData.reduce((sum, item) => sum + item.assessment.total_score, 0);
    return Math.round((totalScore / projectData.length) * 100);
  };

  // Обработчик получения данных по критериям из SummaryCriteriaAnalyticsTable
  const handleCriteriaDataReceived = useCallback((data) => {
    setCriteriaData(data.criteriaData);
    setCriteriaColumns(data.criteriaColumns);
  }, []);

  // Фильтрация данных по типу навыков
  const softData = useMemo(() => {
    return filteredData.filter(item => item.assessment.total_count_soft > 0);
  }, [filteredData]);

  const hardData = useMemo(() => {
    return filteredData.filter(item => item.assessment.total_count_hard > 0);
  }, [filteredData]);

  // Обработчик клика по блоку проекта
  const handleProjectClick = (projectId) => {
    setSelectedProjects(prev => 
      prev.includes(projectId)
        ? prev.filter(id => id !== projectId) // Удаляем, если уже выбран
        : [...prev, projectId] // Добавляем, если не выбран
    );
  };


  return (
    <div className="analytics-container">
      {/* Блоки статистики - сверху */}
      <div className="analytics-ra-header-summary">
        <div className="analytics-ra-stats-container">

          {/* Soft skill */}
          <div className="analytics-ra-stats-block">
            <div className="value">{stats.softCount}</div>
            <div className="title">Количество оценок Soft skill</div>
          </div>

          <div className="analytics-ra-stats-block" style={{color: stats.softAvg >= 97 ? 'green' : stats.softAvg >= 95 ? 'orange' : 'red'}}>
            <div className="value">{stats.softAvg}%</div>
            <div className="title">Средний балл Soft skill</div>
          </div>

          {/* Hard skill */}
          <div className="analytics-ra-stats-block">
            <div className="value">{stats.hardCount}</div>
            <div className="title">Количество оценок Hard skill</div>
          </div>

          <div className="analytics-ra-stats-block" style={{color: stats.hardCount <= 0 ? 'black': stats.hardAvg >= 97 ? 'green' : stats.hardAvg >= 95 ? 'orange' : 'red'}}>
            <div className="value">{stats.hardCount >0 ?stats.hardAvg +'%' : '-'}</div>
            <div className="title">Средний балл Hard skill</div>
          </div>
        </div>
        
        {/* Кнопка отправки отчета - справа от блока статистики */}
        <div className="analytics-ra-send-report-container">
          <button
            type="button"
            className="btn btn-info analytics-ra-send-report-btn"
            onClick={() => setShowSendReportModal(true)}
          >
            <div style={{marginBottom: '5px'}}>📧</div>
            <div>Отправить отчет</div>
          </button>
        </div>
      </div>
      
      {/* Блок проектов - снизу */}
      <div className="analytics-ra-projects-section">
        {projects.length > 0 ? (
          <div className="analytics-ra-projects-container">
            {projects.map(project => (
              <div
                key={project.id}
                onClick={() => handleProjectClick(project.id)}
                className={selectedProjects.includes(project.id) ? 'analytics-ra-project-item selected' : 'analytics-ra-project-item'}
              >
                <h5>{project.name_text}</h5>
                <div>Средний балл: {getProjectAvgScore(project.id)}%</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="analytics-ra-no-projects-message">
            Нет данных по проектам
          </div>
        )}
      </div>
      
      {/* Таблица со средними баллами по критериям Soft skill */}
      <SummaryCriteriaAnalyticsTable data={softData} onCriteriaDataReceived={handleCriteriaDataReceived} tableName="Средний балл по критериям (Soft skill)" skillType="soft" />

      {/* Таблица со средними баллами по критериям Hard skill */}
      <SummaryCriteriaAnalyticsTable data={hardData} onCriteriaDataReceived={handleCriteriaDataReceived} tableName="Средний балл по критериям (Hard skill)" skillType="hard" />

      {/* Модальное окно отправки отчета */}
      <SendReportModal
        show={showSendReportModal}
        onClose={() => setShowSendReportModal(false)}
        selectedProjects={selectedProjects}
        criteriaData={criteriaData}
        criteriaColumns={criteriaColumns}
        filteredData={filteredData}
        totalAssessments={aggregatedData.totalAssessments}
        avgScore={aggregatedData.avgScore}
        dateFrom={dateFrom}
        dateTo={dateTo}
      />
    </div>
  );
};

export default SummaryAnalyticsContent;
